import React, { useEffect, useRef, useState } from 'react';

export interface DeckOffer {
  id: string;
  merchant: string;
  logo: string;
  category: 'dining' | 'travel' | 'groceries' | 'gas' | 'entertainment' | 'retail' | 'other';
  value: string;
  fresh: 'fresh' | 'aging' | 'stale';
}

const L = '/content/cache/logos/';

export const DECK: DeckOffer[] = [
  { id: 'starbucks', merchant: 'Starbucks', logo: `${L}starbucks.svg`, category: 'dining', value: '5% back', fresh: 'fresh' },
  { id: 'delta', merchant: 'Delta', logo: `${L}delta.svg`, category: 'travel', value: '$50 off', fresh: 'fresh' },
  { id: 'target', merchant: 'Target', logo: `${L}target.svg`, category: 'groceries', value: '3% back', fresh: 'aging' },
  { id: 'shell', merchant: 'Shell', logo: `${L}shell.svg`, category: 'gas', value: '10% back', fresh: 'fresh' },
  { id: 'netflix', merchant: 'Netflix', logo: `${L}netflix.svg`, category: 'entertainment', value: '$10 off', fresh: 'stale' },
];

const REASONS: Record<DeckOffer['category'], string> = {
  dining: 'Moved up, you use dining offers often',
  travel: 'Moved up, you use travel offers often',
  groceries: 'Moved up, you use grocery offers often',
  gas: 'Moved up, you use gas offers often',
  entertainment: 'Moved up, you use entertainment offers often',
  retail: 'Moved up, you use retail offers often',
  other: 'Moved up, based on recent activity',
};

interface DeckReshuffleProps {
  interval?: number;
  autoPlay?: boolean;
  size?: 'default' | 'compact';
}

const DeckReshuffle: React.FC<DeckReshuffleProps> = ({ interval = 4200, autoPlay = true, size = 'default' }) => {
  const [order, setOrder] = useState<string[]>(DECK.map(d => d.id));
  const [label, setLabel] = useState<{ text: string; visible: boolean }>({ text: '', visible: false });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const labelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!autoPlay) return;
    timerRef.current = setInterval(() => {
      setOrder(prev => {
        const [front, ...rest] = prev;
        const promotedIndex = Math.floor(Math.random() * rest.length);
        const promoted = rest[promotedIndex];
        const next = [promoted, front, ...rest.filter((_, i) => i !== promotedIndex)];
        const card = DECK.find(d => d.id === promoted);
        if (card) {
          setLabel({ text: REASONS[card.category], visible: true });
          if (labelTimeoutRef.current) clearTimeout(labelTimeoutRef.current);
          labelTimeoutRef.current = setTimeout(() => setLabel(l => ({ ...l, visible: false })), 2000);
        }
        return next;
      });
    }, interval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (labelTimeoutRef.current) clearTimeout(labelTimeoutRef.current);
    };
  }, [autoPlay, interval]);

  return (
    <div className={`cache__deck cache__deck--${size}`}>
      <div className="cache__deck-stage">
        {DECK.map(card => {
          const pos = order.indexOf(card.id);
          return (
            <div key={card.id} className="cache__deck-card" data-pos={pos}>
              <div className="cache__deck-card-top">
                <span className={`cache__deck-tag cache__deck-tag--${card.category}`}>{card.category}</span>
                <span className={`cache__deck-dot cache__deck-dot--${card.fresh}`} aria-hidden="true" />
                <span className="cache__sr-only">{`freshness: ${card.fresh}`}</span>
              </div>
              <div className="cache__deck-brand">
                <img className="cache__deck-logo" src={card.logo} alt="" aria-hidden="true" />
                <div className="cache__deck-merchant">{card.merchant}</div>
              </div>
              <div className="cache__deck-value">{card.value}</div>
            </div>
          );
        })}
      </div>
      <div className={`cache__deck-label ${label.visible ? 'is-visible' : ''}`} role="status">
        {label.text}
      </div>
    </div>
  );
};

export default DeckReshuffle;
